/**
 * useAccountFeatures — RBAC-6.1. Which registry features the ACTIVE account has switched on.
 *
 * A render seam only (see lib/accountFeatures.ts): the database gate is `account_has_module()`. One read
 * of `my_account_modules()` per account, shared through accountFeaturesCache. `isOn(key)` is closed by
 * default: while loading, on a failed read, and for an unknown key it answers false.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTenant } from '@/contexts/TenantContext';
import { isModuleActive, type AccountModuleStatus } from '@/lib/accountFeatures';
import {
  ACCOUNT_FEATURES_CHANGED,
  getOrStartAccountFeaturesFetch,
  peekAccountFeatures,
} from '@/lib/accountFeaturesCache';
import { fetchMyAccountModules } from '@/services/accountModulesService';

export interface AccountFeatures {
  features: AccountModuleStatus[] | null;
  loading: boolean;
  isOn: (key: string) => boolean;
}

export function useAccountFeatures(): AccountFeatures {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.tenant_id;

  const seeded = peekAccountFeatures(tenantId);
  const [features, setFeatures] = useState<AccountModuleStatus[] | null>(seeded?.resolved ? seeded.features : null);
  const [loading, setLoading] = useState(!seeded?.resolved);

  const [generation, setGeneration] = useState(0);
  useEffect(() => {
    const bump = () => setGeneration((g) => g + 1);
    window.addEventListener(ACCOUNT_FEATURES_CHANGED, bump);
    return () => window.removeEventListener(ACCOUNT_FEATURES_CHANGED, bump);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const peek = peekAccountFeatures(tenantId);
    if (peek?.resolved) {
      setFeatures(peek.features);
      setLoading(false);
    } else {
      setFeatures(null);
      setLoading(true);
    }
    if (!tenantId) return () => { cancelled = true; };
    getOrStartAccountFeaturesFetch(tenantId, fetchMyAccountModules).then((f) => {
      if (!cancelled) {
        setFeatures(f);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [tenantId, generation]);

  const isOn = useCallback((key: string) => isModuleActive(features, key), [features]);
  return { features, loading, isOn };
}
